import React, { useCallback } from 'react';

import { Text } from '@rbilabs/universal-components';
import { Image, ImageProps } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useIntl } from 'react-intl';
import { View } from 'react-native';

import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import { Calories } from 'components/price-and-calories';
import { getStaticPathname } from 'navigation/location/location';
import { primitive } from 'styles/constants/primitives';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import { menuSectionStyles } from '../menu-section/styled';

import {
  PriceAndCaloriesListWrapper,
  TileDescription,
  TileHeader,
  TileImage,
  TileImageWrapper,
} from './styled';
import { IMenuTileProps, IPriceAndCaloriesProps } from './types';

const ItemCalories = Text.withConfig({
  numberOfLines: 1,
  color: Styles.color.tertiary,
  marginTop: '1',
  marginBottom: '0',
  textTransform: 'capitalize',
});

const StyledItemPrice = Text.withConfig({
  fontWeight: 'bold',
  color: Styles.color.pricing,
  marginTop: '1',
  marginRight: {
    base: '1',
    md: '0',
  },
});

const priceAndCalories = ({
  calories,
  price,
  type,
}: IPriceAndCaloriesProps): JSX.Element | null => {
  const shouldRenderPriceAndCalories = price || calories;
  if (type === 'section' || !shouldRenderPriceAndCalories) {
    return null;
  }

  return (
    <PriceAndCaloriesListWrapper>
      <StyledItemPrice>{price}</StyledItemPrice>
      <Calories>
        <ItemCalories>{calories}</ItemCalories>
      </Calories>
    </PriceAndCaloriesListWrapper>
  );
};

const MenuTile = React.memo(
  ({
    image,
    objectFitContain,
    calories,
    name,
    type,
    price,
    id,
    accessibilityLabel = '',
    accessibilityLevel = 3,
    accessibilityRole = 'header',
  }: IMenuTileProps) => {
    const {
      menu: menuAppTakeover,
      menuSection: menuSectionAppTakeover,
      pdp: pdpAppTakeover,
      fontFamily,
    } = useAppTakeover();
    const isTargetSection = menuAppTakeover?.targetSectionIds.includes(id);
    const isTargetItem = pdpAppTakeover?.targetMenuItemIds.includes(id);

    const { formatMessage } = useIntl();

    const priceAndCaloriesContent = priceAndCalories({ type, price, calories });

    const handleOnImageLoaded = useCallback(() => {
      emitEvent(EventName.MENU_TILE_IMAGE_LOADED, {
        sanityId: id,
        isOnMainMenu: getStaticPathname() === routes.menu,
        attributes: [],
      });
    }, [id]);

    const Element = isTargetSection ? LinearGradient : View;

    return (
      <>
        <Element
          {...((menuAppTakeover?.sectionGradientProps as any) || {})}
          style={[
            menuSectionStyles.menuSectionRow,
            {
              backgroundColor: isTargetItem
                ? menuSectionAppTakeover?.itemBackgroundColor
                : primitive.$white,
            },
          ]}
          testID="menu-tile-bk"
          accessibilityRole={accessibilityRole}
          accessibilityLevel={accessibilityLevel}
          accessibilityLabel={accessibilityLabel}
        >
          <TileImageWrapper>
            <TileImage
              image={image}
              alt={''}
              objectFitContain={objectFitContain}
              onImageLoaded={handleOnImageLoaded}
              accessibilityHint={formatMessage({ id: 'select' })}
            />
          </TileImageWrapper>
          <TileDescription>
            <TileHeader
              testID={`tile-header-${name?.toLowerCase().replace(/\s/g, '')}`}
              fontFamily={isTargetItem ? fontFamily : undefined}
            >
              {name}
            </TileHeader>
            {priceAndCaloriesContent}
          </TileDescription>
        </Element>
        {isTargetSection && menuAppTakeover?.sectionImage && (
          <Image
            source={menuAppTakeover?.sectionImage}
            style={menuAppTakeover?.sectionImageStyle as ImageProps['style']}
          />
        )}
      </>
    );
  }
);

export default MenuTile;
